import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Chip, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid, MenuItem,
  CircularProgress, Tooltip, InputAdornment, Checkbox, ListItemText,
  OutlinedInput, Select, FormControl, InputLabel, FormHelperText,
  Alert
} from '@mui/material';
import { Add, Edit, Delete, Search, Grade as MarksIcon, SelectAll } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { assessmentsAPI, settingsAPI, subjectsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const TYPES = ['exam', 'test', 'assignment', 'project', 'quiz', 'practical', 'cat'];

const EMPTY = {
  title: '', assessment_type: 'test',
  subject_ids: [], class_ids: [],
  academic_year_id: '', max_score: 100,
  assessment_date: '', weight_percent: 100,
};

export default function Assessments() {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { can, user } = useAuth();

  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const [classes, setClasses] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [filterYear, setFilterYear] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await assessmentsAPI.getAll({
        academic_year_id: filterYear || undefined,
        class_id: filterClass || undefined
      });
      if (res.data.success) setAssessments(res.data.data);
    } finally { setLoading(false); }
  }, [filterYear, filterClass]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    settingsAPI.getClasses().then(r => { if (r.data.success) setClasses(r.data.data); }).catch(() => {});
    settingsAPI.getAcademicYears().then(r => {
      if (r.data.success) {
        setAcademicYears(r.data.data);
        const cur = r.data.data.find(y => y.is_current);
        if (cur) { setFilterYear(cur.id); setForm(f => ({ ...f, academic_year_id: cur.id })); }
      }
    }).catch(() => {});
    subjectsAPI.getAll().then(r => { if (r.data.success) setSubjects(r.data.data); }).catch(() => {});
  }, []);

  const openCreate = () => {
    setEditing(null);
    const cur = academicYears.find(y => y.is_current);
    setForm({ ...EMPTY, academic_year_id: cur?.id || '' });
    setDialogOpen(true);
  };

  const openEdit = async (a) => {
    setEditing(a);
    try {
      const res = await assessmentsAPI.getById(a.id);
      const data = res.data.data;
      setForm({
        title: data.title || '', assessment_type: data.assessment_type || 'test',
        subject_ids: [data.subject_id], // single subject per assessment record
        class_ids: data.classes?.map(c => c.id) || [],
        academic_year_id: data.academic_year_id || '',
        max_score: data.max_score || 100,
        assessment_date: data.assessment_date?.split('T')[0] || '',
        weight_percent: data.weight_percent || 100,
      });
    } catch { setForm({ ...EMPTY }); }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title) return enqueueSnackbar('Title is required', { variant: 'warning' });
    if (!form.subject_ids.length) return enqueueSnackbar('Select at least one subject', { variant: 'warning' });
    if (!form.class_ids.length) return enqueueSnackbar('Select at least one class', { variant: 'warning' });
    if (!form.academic_year_id) return enqueueSnackbar('Academic year is required', { variant: 'warning' });

    setSaving(true);
    try {
      const payload = {
        ...form,
        subject_ids: JSON.stringify(form.subject_ids),
        class_ids: JSON.stringify(form.class_ids),
      };
      if (editing) await assessmentsAPI.update(editing.id, { ...payload, class_ids: JSON.stringify(form.class_ids) });
      else await assessmentsAPI.create(payload);
      enqueueSnackbar(editing ? 'Assessment updated' : 'Assessment(s) created', { variant: 'success' });
      setDialogOpen(false);
      load();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Error saving', { variant: 'error' });
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this assessment and all its marks?')) return;
    try {
      await assessmentsAPI.delete(id);
      enqueueSnackbar('Assessment deleted', { variant: 'success' });
      load();
    } catch { enqueueSnackbar('Error deleting', { variant: 'error' }); }
  };

  const handleSelectAll = (field, allItems, idKey = 'id') => {
    const allIds = allItems.map(i => i[idKey]);
    setForm(f => ({
      ...f,
      [field]: f[field].length === allIds.length ? [] : allIds
    }));
  };

  const typeColor = (t) => ({ exam:'error', test:'warning', assignment:'info', project:'success', quiz:'default', practical:'secondary', cat:'primary' }[t] || 'default');

  const filtered = assessments.filter(a =>
    !search || a.title?.toLowerCase().includes(search.toLowerCase()) ||
    a.subject_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" fontWeight={700}>Assessments Management</Typography>
        {can('assessments', 'full') && <Button variant="contained" startIcon={<Add />} onClick={openCreate}>Add Assessment</Button>}
      </Box>

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ pb: '12px !important' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField placeholder="Search assessments..." value={search} onChange={e => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField select label="Academic Year" value={filterYear} onChange={e => setFilterYear(e.target.value)}>
                <MenuItem value="">All Years</MenuItem>
                {(academicYears.some(y => y.is_current) ? academicYears.filter(y => y.is_current) : academicYears).map(y => (
                <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term} (Current)</MenuItem>
              ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField select label="Class" value={filterClass} onChange={e => setFilterClass(e.target.value)}>
                <MenuItem value="">All Classes</MenuItem>
                {classes.map(c => <MenuItem key={c.id} value={c.id}>{c.name}{c.stream ? ` (${c.stream})` : ''}</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Classes</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Max</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Entries</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6 }}><CircularProgress /></TableCell></TableRow>
                : filtered.length === 0
                ? <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>No assessments found</TableCell></TableRow>
                : filtered.map(a => (
                  <TableRow key={a.id} hover>
                    <TableCell><Typography fontSize={14} fontWeight={600}>{a.title}</Typography></TableCell>
                    <TableCell><Chip label={a.assessment_type} size="small" color={typeColor(a.assessment_type)} sx={{ textTransform: 'capitalize' }} /></TableCell>
                    <TableCell>
                      <Chip label={a.subject_code || a.subject_name} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography fontSize={12} color="text.secondary" sx={{ maxWidth: 200 }} noWrap>
                        {a.class_names || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>{a.assessment_date ? new Date(a.assessment_date).toLocaleDateString() : '—'}</TableCell>
                    <TableCell>{a.max_score}</TableCell>
                    <TableCell><Chip label={a.entries_count || 0} size="small" color={a.entries_count > 0 ? 'success' : 'default'} /></TableCell>
                    <TableCell align="right">
                      <Tooltip title="Enter Marks">
                        <IconButton size="small" color="primary" onClick={() => navigate(`/marks?assessment_id=${a.id}`)}>
                          <MarksIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {can('assessments', 'full') && <>
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(a)}><Edit fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(a.id)}><Delete fontSize="small" /></IconButton></Tooltip>
                      </>}
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* ── Assessment Dialog ── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Assessment' : 'Add Assessment'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>

            <Grid item xs={12}>
              <TextField label="Title *" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField select label="Type *" value={form.assessment_type} onChange={e => setForm({ ...form, assessment_type: e.target.value })}>
                {TYPES.map(t => <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>{t.toUpperCase()}</MenuItem>)}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField select label="Academic Year *" value={form.academic_year_id} onChange={e => setForm({ ...form, academic_year_id: e.target.value })}>
                {(academicYears.some(y => y.is_current) ? academicYears.filter(y => y.is_current) : academicYears).map(y => (
                <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term} (Current)</MenuItem>
              ))}
              </TextField>
            </Grid>

            {/* Multi-select Subjects */}
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Subject(s) *</InputLabel>
                <Select
                  multiple value={form.subject_ids}
                  onChange={e => setForm({ ...form, subject_ids: e.target.value })}
                  input={<OutlinedInput label="Subject(s) *" />}
                  renderValue={(sel) => sel.map(id => subjects.find(s => s.id === id)?.name).filter(Boolean).join(', ')}
                >
                  <MenuItem onClick={() => handleSelectAll('subject_ids', subjects)} sx={{ fontStyle: 'italic', color: 'primary.main' }}>
                    <SelectAll fontSize="small" sx={{ mr: 1 }} />
                    {form.subject_ids.length === subjects.length ? 'Deselect All' : 'Select All'}
                  </MenuItem>
                  {subjects.map(s => (
                    <MenuItem key={s.id} value={s.id}>
                      <Checkbox size="small" checked={form.subject_ids.includes(s.id)} />
                      <ListItemText primary={s.name} secondary={s.code} />
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {form.subject_ids.length} selected
                  {!editing && form.subject_ids.length > 1 && ` — will create ${form.subject_ids.length} assessment records`}
                </FormHelperText>
              </FormControl>
            </Grid>

            {/* Multi-select Classes */}
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Class(es) *</InputLabel>
                <Select
                  multiple value={form.class_ids}
                  onChange={e => setForm({ ...form, class_ids: e.target.value })}
                  input={<OutlinedInput label="Class(es) *" />}
                  renderValue={(sel) => sel.map(id => {
                    const c = classes.find(c => c.id === id);
                    return c ? `${c.name}${c.stream ? ' '+c.stream : ''}` : '';
                  }).filter(Boolean).join(', ')}
                >
                  <MenuItem onClick={() => handleSelectAll('class_ids', classes)} sx={{ fontStyle: 'italic', color: 'primary.main' }}>
                    <SelectAll fontSize="small" sx={{ mr: 1 }} />
                    {form.class_ids.length === classes.length ? 'Deselect All' : 'Select All'}
                  </MenuItem>
                  {classes.map(c => (
                    <MenuItem key={c.id} value={c.id}>
                      <Checkbox size="small" checked={form.class_ids.includes(c.id)} />
                      <ListItemText primary={`${c.name}${c.stream ? ' ('+c.stream+')' : ''}`} />
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{form.class_ids.length} class(es) selected</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField label="Max Score" type="number" value={form.max_score}
                onChange={e => setForm({ ...form, max_score: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Weight %" type="number" value={form.weight_percent}
                onChange={e => setForm({ ...form, weight_percent: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Assessment Date" type="date" value={form.assessment_date}
                onChange={e => setForm({ ...form, assessment_date: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>

            {!editing && form.subject_ids.length > 1 && (
              <Grid item xs={12}>
                <Alert severity="info">
                  Creating <strong>{form.subject_ids.length}</strong> assessments (one per subject), each linked to {form.class_ids.length} class(es).
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
